export const SCOPED_ACCESS_RETURN_TO_PARAM = 'scopedAccessReturnTo'
export const SCOPED_ACCESS_ENTRY_RETURN_TO_PARAM = 'returnTo'
export const SCOPED_ACCESS_EXPECTED_COMPANY_ID_PARAM = 'scopedAccessExpectedCompanyId'

const SCOPED_ACCESS_RETURN_TO_STORAGE_KEY = 'scoped-access:return-to'
const SCOPED_ACCESS_EXPECTED_COMPANY_ID_STORAGE_KEY = 'scoped-access:expected-company-id'
const SCOPED_ACCESS_OPERATION_LOCK_STORAGE_KEY = 'scoped-access:operation-lock'
const ALLOWED_SCOPED_ACCESS_RETURN_TO_PREFIXES = ['/support/', '/partner/']
const MISSING_SCOPED_ACCESS_RETURN_TO_ERROR = 'Missing scoped access return origin.'
const SCOPED_ACCESS_ENDED_CODE = 'SCOPED_ACCESS_ENDED'
const SCOPED_ACCESS_ENDED_EVENT_NAME = 'scoped-access-ended'
const SCOPED_ACCESS_CHANNEL_NAME = 'scoped-access'
const SCOPED_ACCESS_OPERATION_LOCK_TTL_MS = 30000
const SCOPED_ACCESS_OPERATION_LOCK_RETRY_MS = 75

type ScopedAccessChannelMessage = {
    type: typeof SCOPED_ACCESS_ENDED_EVENT_NAME
    sourceTabId: string
}

type ScopedAccessOperationLock = {
    ownerId: string
    expiresAt: number
}

type ProblemDetailsLike = {
    code?: unknown
    extensions?: Record<string, unknown> | null
}

// Return-origin bootstrap and storage
const normalizeScopedAccessReturnTo = (value: string | null | undefined): string | null => {
    if (!value) {
        return null
    }

    try {
        const parsedUrl = new URL(value, window.location.origin)
        if (
            parsedUrl.origin !== window.location.origin ||
            !ALLOWED_SCOPED_ACCESS_RETURN_TO_PREFIXES.some((prefix) => parsedUrl.pathname.startsWith(prefix))
        ) {
            return null
        }

        return `${parsedUrl.pathname}${parsedUrl.search}` || null
    } catch {
        return null
    }
}

export const readScopedAccessReturnToFromSearch = (search: string): string | null => {
    const searchParams = new URLSearchParams(search)

    return normalizeScopedAccessReturnTo(searchParams.get(SCOPED_ACCESS_RETURN_TO_PARAM))
}

export const readScopedAccessEntryReturnToFromSearch = (search: string): string | null => {
    const searchParams = new URLSearchParams(search)

    return normalizeScopedAccessReturnTo(searchParams.get(SCOPED_ACCESS_ENTRY_RETURN_TO_PARAM))
}

const normalizeScopedAccessExpectedCompanyId = (value: string | null | undefined): string | null => {
    const normalizedValue = value?.trim()

    return normalizedValue || null
}

export const readScopedAccessExpectedCompanyIdFromSearch = (search: string): string | null => {
    const searchParams = new URLSearchParams(search)

    return normalizeScopedAccessExpectedCompanyId(searchParams.get(SCOPED_ACCESS_EXPECTED_COMPANY_ID_PARAM))
}

export const storeScopedAccessReturnTo = (returnTo: string): void => {
    const normalizedReturnTo = normalizeScopedAccessReturnTo(returnTo)
    if (!normalizedReturnTo) {
        return
    }

    window.sessionStorage.setItem(SCOPED_ACCESS_RETURN_TO_STORAGE_KEY, normalizedReturnTo)
    window.localStorage.setItem(SCOPED_ACCESS_RETURN_TO_STORAGE_KEY, normalizedReturnTo)
}

export const readScopedAccessReturnTo = (): string | null =>
    normalizeScopedAccessReturnTo(window.sessionStorage.getItem(SCOPED_ACCESS_RETURN_TO_STORAGE_KEY)) ??
    normalizeScopedAccessReturnTo(window.localStorage.getItem(SCOPED_ACCESS_RETURN_TO_STORAGE_KEY))

export const clearScopedAccessReturnTo = (): void => {
    window.sessionStorage.removeItem(SCOPED_ACCESS_RETURN_TO_STORAGE_KEY)
    window.localStorage.removeItem(SCOPED_ACCESS_RETURN_TO_STORAGE_KEY)
}

export const storeScopedAccessExpectedCompanyId = (companyId: string): void => {
    const normalizedCompanyId = normalizeScopedAccessExpectedCompanyId(companyId)
    if (!normalizedCompanyId) {
        return
    }

    window.sessionStorage.setItem(SCOPED_ACCESS_EXPECTED_COMPANY_ID_STORAGE_KEY, normalizedCompanyId)
}

export const readScopedAccessExpectedCompanyId = (): string | null =>
    normalizeScopedAccessExpectedCompanyId(window.sessionStorage.getItem(SCOPED_ACCESS_EXPECTED_COMPANY_ID_STORAGE_KEY))

export const clearScopedAccessExpectedCompanyId = (): void => {
    window.sessionStorage.removeItem(SCOPED_ACCESS_EXPECTED_COMPANY_ID_STORAGE_KEY)
}

export const getScopedAccessReturnTo = (): string => {
    const returnTo = readScopedAccessReturnTo()

    if (!returnTo) {
        throw new Error(MISSING_SCOPED_ACCESS_RETURN_TO_ERROR)
    }

    return returnTo
}

const createScopedAccessId = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID()
    }

    return `scoped-access-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const delay = (milliseconds: number): Promise<void> =>
    new Promise((resolve) => {
        window.setTimeout(resolve, milliseconds)
    })

const readScopedAccessOperationLock = (): ScopedAccessOperationLock | null => {
    try {
        const value = window.localStorage.getItem(SCOPED_ACCESS_OPERATION_LOCK_STORAGE_KEY)
        if (!value) {
            return null
        }

        const operationLock = JSON.parse(value) as Partial<ScopedAccessOperationLock>

        return typeof operationLock.ownerId === 'string' && typeof operationLock.expiresAt === 'number'
            ? {
                  ownerId: operationLock.ownerId,
                  expiresAt: operationLock.expiresAt,
              }
            : null
    } catch {
        return null
    }
}

const tryAcquireScopedAccessOperationLock = (ownerId: string): boolean => {
    const currentLock = readScopedAccessOperationLock()
    if (currentLock && currentLock.expiresAt > Date.now()) {
        return currentLock.ownerId === ownerId
    }

    window.localStorage.setItem(
        SCOPED_ACCESS_OPERATION_LOCK_STORAGE_KEY,
        JSON.stringify({
            ownerId,
            expiresAt: Date.now() + SCOPED_ACCESS_OPERATION_LOCK_TTL_MS,
        } satisfies ScopedAccessOperationLock)
    )

    return readScopedAccessOperationLock()?.ownerId === ownerId
}

const acquireScopedAccessOperationLock = async (ownerId: string): Promise<void> => {
    let isOperationLockAcquired = false

    while (!isOperationLockAcquired) {
        if (tryAcquireScopedAccessOperationLock(ownerId)) {
            isOperationLockAcquired = true
        } else {
            await delay(SCOPED_ACCESS_OPERATION_LOCK_RETRY_MS)
        }
    }
}

const releaseScopedAccessOperationLock = (ownerId: string): void => {
    if (readScopedAccessOperationLock()?.ownerId === ownerId) {
        window.localStorage.removeItem(SCOPED_ACCESS_OPERATION_LOCK_STORAGE_KEY)
    }
}

export const runWithScopedAccessOperationLock = async <T>(operation: () => Promise<T>): Promise<T> => {
    const ownerId = createScopedAccessId()
    await acquireScopedAccessOperationLock(ownerId)

    try {
        return await operation()
    } finally {
        releaseScopedAccessOperationLock(ownerId)
    }
}

// Admin entry and exit redirects
const buildScopedAccessAdminDashboardUrl = (returnTo: string, expectedCompanyId?: string | null): string => {
    const adminUrl = new URL('/admin/dashboard', window.location.origin)
    // This is a one-time bootstrap handoff param. Admin normalizes it into browser-shared storage and removes it from the URL.
    adminUrl.searchParams.set(SCOPED_ACCESS_RETURN_TO_PARAM, returnTo)
    if (expectedCompanyId) {
        adminUrl.searchParams.set(SCOPED_ACCESS_EXPECTED_COMPANY_ID_PARAM, expectedCompanyId)
    }

    return adminUrl.toString()
}

export const redirectToScopedAccessAdminDashboard = (returnTo: string, expectedCompanyId?: string | null): void => {
    window.location.assign(buildScopedAccessAdminDashboardUrl(returnTo, expectedCompanyId))
}

export const redirectToStoredScopedAccessReturnTo = (): void => {
    const returnTo = getScopedAccessReturnTo()
    window.location.assign(returnTo)
}

// Scoped-access response interpretation
const getProblemDetailsCode = (data: unknown): string | null => {
    if (!data || typeof data !== 'object') {
        return null
    }

    const problem = data as ProblemDetailsLike
    if (typeof problem.code === 'string') {
        return problem.code
    }

    const extensionCode = problem.extensions?.code
    return typeof extensionCode === 'string' ? extensionCode : null
}

export const isScopedAccessEndedResponse = (error: { status?: unknown; data?: unknown } | null | undefined): boolean =>
    Number(error?.status) === 409 && getProblemDetailsCode(error?.data)?.toUpperCase() === SCOPED_ACCESS_ENDED_CODE

// Cross-tab invalidation transport
const scopedAccessTabId = createScopedAccessId()

const createScopedAccessChannel = (): BroadcastChannel | null =>
    typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(SCOPED_ACCESS_CHANNEL_NAME)

export const notifyScopedAccessEnded = (): void => {
    window.dispatchEvent(new Event(SCOPED_ACCESS_ENDED_EVENT_NAME))
}

export const broadcastScopedAccessEnded = (): void => {
    const scopedAccessChannel = createScopedAccessChannel()
    scopedAccessChannel?.postMessage({
        type: SCOPED_ACCESS_ENDED_EVENT_NAME,
        sourceTabId: scopedAccessTabId,
    } satisfies ScopedAccessChannelMessage)
    scopedAccessChannel?.close()
}

export const addScopedAccessEndedListener = (listener: () => void): (() => void) => {
    const scopedAccessChannel = createScopedAccessChannel()
    const handleChannelMessage = (event: MessageEvent<ScopedAccessChannelMessage>) => {
        if (
            event.data?.type === SCOPED_ACCESS_ENDED_EVENT_NAME &&
            typeof event.data.sourceTabId === 'string' &&
            event.data.sourceTabId !== scopedAccessTabId
        ) {
            listener()
        }
    }

    window.addEventListener(SCOPED_ACCESS_ENDED_EVENT_NAME, listener)
    scopedAccessChannel?.addEventListener('message', handleChannelMessage)

    return () => {
        window.removeEventListener(SCOPED_ACCESS_ENDED_EVENT_NAME, listener)
        scopedAccessChannel?.removeEventListener('message', handleChannelMessage)
        scopedAccessChannel?.close()
    }
}
